import { createTheme, ThemeOptions } from '@mui/material/styles';

/**
 * Fixd.ai Theme
 *
 * Color scheme inspired by TrustFix:
 * - Primary: Vibrant Blue (#2D7FF9)
 * - Secondary: Purple (#667eea)
 * - Accent: Deep Purple (#764ba2)
 *
 * Design philosophy: Clean, modern, trustworthy
 */

const fixdaiThemeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: '#2D7FF9',
      light: '#5FA3FF',
      dark: '#1A5FD9',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#667eea',
      light: '#8BA0F0',
      dark: '#4A5DC8',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#E63946',
      light: '#FF6B77',
      dark: '#C62828',
    },
    warning: {
      main: '#F77F00',
      light: '#FFB347',
      dark: '#D65F00',
    },
    info: {
      main: '#0077B6',
      light: '#48A3D7',
      dark: '#005A8D',
    },
    success: {
      main: '#06A77D',
      light: '#4ECDC4',
      dark: '#048661',
    },
    background: {
      default: '#FFFFFF',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1F2937',
      secondary: '#6B7280',
      disabled: '#9CA3AF',
    },
    divider: '#E0E0E0',
  },
  typography: {
    fontFamily: '"Matter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Matter", sans-serif',
      fontWeight: 700,
      fontSize: '3rem',
      lineHeight: 1.2,
      color: '#1F2937',
    },
    h2: {
      fontFamily: '"Matter", sans-serif',
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.3,
      color: '#1F2937',
    },
    h3: {
      fontFamily: '"Matter", sans-serif',
      fontWeight: 600,
      fontSize: '2rem',
      lineHeight: 1.4,
      color: '#1F2937',
    },
    h4: {
      fontFamily: '"Matter", sans-serif',
      fontWeight: 600,
      fontSize: '1.75rem',
      lineHeight: 1.4,
      color: '#1F2937',
    },
    h5: {
      fontFamily: '"Matter", sans-serif',
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.5,
      color: '#1F2937',
    },
    h6: {
      fontFamily: '"Matter", sans-serif',
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.5,
      color: '#1F2937',
    },
    body1: {
      fontFamily: '"Matter", sans-serif',
      fontSize: '1rem',
      lineHeight: 1.6,
      color: '#6B7280',
    },
    body2: {
      fontFamily: '"Matter", sans-serif',
      fontSize: '0.875rem',
      lineHeight: 1.6,
      color: '#6B7280',
    },
    button: {
      fontFamily: '"Matter", sans-serif',
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          fontSize: '1rem',
          fontWeight: 600,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(45, 127, 249, 0.2)',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0 4px 12px rgba(45, 127, 249, 0.3)',
          },
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #2D7FF9 0%, #667eea 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #1A5FD9 0%, #4A5DC8 100%)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
        },
        colorPrimary: {
          backgroundColor: '#EFF6FF',
          color: '#2D7FF9',
          '&:hover': {
            backgroundColor: '#DBEAFE',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
              borderColor: '#2D7FF9',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#2D7FF9',
            },
          },
        },
      },
    },
  },
};

export const fixdaiTheme = createTheme(fixdaiThemeOptions);
